import { z } from 'zod';
import { contentIdSchema } from './common.js';
import { playbackModeSchema } from './settings.js';
/**
 * Контракт playback session.
 *
 * Ключевое правило: клиент передаёт ТОЛЬКО внутренние ссылки
 * (`contentId`, `sourceId`, `fileId`). Произвольные magnet/torrent/URL
 * не принимаются — иначе API превращается в SSRF/open proxy.
 *
 * Поток всегда выдаётся через Media Gateway нашего домена
 * (`/media/<sessionId>/...`), upstream origin клиенту не виден.
 */
export const sourceIdSchema = z.string().regex(/^provider:[a-z0-9-]{1,32}:[A-Za-z0-9_-]{1,80}$/);
const fileIdSchema = z.string().regex(/^file:[A-Za-z0-9_-]{1,80}$/);
/**
 * Opaque id playback-сессии. Формат публичен, значение — нет: угадать его
 * нельзя, а принадлежность сессии всё равно проверяется на сервере.
 */
export const playbackSessionIdSchema = z.string().regex(/^pbs_[a-f0-9]{24}$/);
/**
 * Capabilities клиента (PLAYBACK_TRANSCODING.md). Сервер использует их для
 * выбора минимально затратного режима, но окончательное решение принимает сам.
 */
export const clientCapabilitiesSchema = z.object({
    /**
     * Класс клиента. Нужен не для украшательства логов: часть решений сервера
     * выведена из поведения БРАУЗЕРОВ (вето на DTS/TrueHD, которые Chromium
     * молча отбрасывает), и к Android TV они неприменимы — там кодеки берутся
     * из `MediaCodecList`, то есть измеряются. Умолчание `web` сохраняет прежнее
     * поведение для клиентов, которые о платформе не сказали.
     */
    platform: z.enum(['web', 'android-tv']).default('web'),
    containers: z.array(z.string().max(16)).max(24).default(['mp4', 'webm']),
    videoCodecs: z.array(z.string().max(24)).max(24).default(['h264']),
    /**
     * Видеокодеки, которые берёт именно Media Source Extensions.
     *
     * Отдельно от `videoCodecs`, потому что нативный плеер и MSE поддерживают
     * РАЗНОЕ: HEVC часто играется через `<video src>` и не принимается
     * MediaSource. Сервер выбирает способ доставки (непрерывный поток или
     * нарезка через hls.js), и объединённый ответ его обманывает — плейлист
     * уезжает клиенту с кодеком, который живёт у того только в нативном пути.
     * Пустой список — клиент не сказал; тогда судим по `videoCodecs`.
     */
    mseVideoCodecs: z.array(z.string().max(24)).max(24).default([]),
    audioCodecs: z.array(z.string().max(24)).max(24).default(['aac']),
    maxHeight: z.number().int().min(240).max(4320).default(1080),
    maxFrameRate: z.number().int().min(24).max(120).default(60),
    hdr: z.boolean().default(false),
    subtitleFormats: z.array(z.string().max(16)).max(12).default(['vtt']),
    drm: z.array(z.string().max(24)).max(8).default([]),
});
export const playbackSessionRequestSchema = z.object({
    contentId: contentIdSchema,
    /** Внутренний id источника, ранее выданный нашим provider layer. */
    sourceId: sourceIdSchema.optional(),
    /** Внутренний id файла внутри источника. */
    fileId: fileIdSchema.optional(),
    quality: z.enum(['auto', '480p', '720p', '1080p']).default('auto'),
    capabilities: clientCapabilitiesSchema.optional(),
    /**
     * Какую серию играть. Нужны обе части: по одному номеру серии файл в раздаче
     * сезона не найти, а угадывать «первый подходящий» нельзя — включится не то.
     */
    season: z.number().int().nonnegative().optional(),
    episode: z.number().int().positive().optional(),
    /**
     * Какую озвучку играть у онлайн-источника, по её названию из списка озвучек
     * источника. Пусто — озвучка по умолчанию. У торрентов не используется.
     */
    voice: z.string().max(120).optional(),
    /**
     * Какую звуковую дорожку сводить, если поток придётся перекодировать, по её
     * номеру среди звуковых. Смысл есть только для перекодирования: при прямой
     * отдаче все дорожки едут внутри потока, и выбирает их сам плеер.
     */
    audioTrack: z.number().int().nonnegative().max(63).optional(),
});
/** Объяснение выбранного режима: показывается в диагностике, а не в UI. */
export const playbackDecisionSchema = z.object({
    mode: playbackModeSchema,
    reason: z.string(),
    /** Какое ускорение реально задействовано; `none` — прямая отдача файла. */
    hardware: z.enum(['vaapi', 'cpu', 'none']),
    videoCodec: z.string().nullable(),
    audioCodec: z.string().nullable(),
    container: z.string(),
});
export const playbackSubtitleSchema = z.object({
    id: z.string(),
    language: z.string(),
    label: z.string(),
    format: z.enum(['vtt', 'srt', 'ass']),
    /**
     * Дорожка «форсированных» субтитров: она переводит не речь, а надписи и
     * реплики на чужом языке — то, что зритель, смотрящий в дубляже, всё равно
     * хочет прочитать. Такую включают САМИ, без спроса; обычные — по просьбе.
     *
     * Признак ставит сервер: у встроенной дорожки он лежит в диспозиции
     * контейнера (`forced`), а у внешней — в её названии («Forced», «Надписи»),
     * и разбирать это в двух клиентах по-разному значило бы получить два разных
     * ответа на один вопрос.
     */
    forced: z.boolean().default(false),
    /** Всегда путь нашего gateway. */
    url: z.string().startsWith('/media/'),
});
export const playbackSessionResponseSchema = z.object({
    sessionId: z.string(),
    contentId: contentIdSchema,
    /**
     * Источник, которым сессия в итоге играет. Может отличаться от запрошенного:
     * при отказе сервер перебирает соседние источники сам, и клиент должен
     * узнать, чем всё кончилось, — иначе он покажет выбранным то, что не играет.
     */
    sourceId: sourceIdSchema.nullable().default(null),
    /** Всегда путь нашего Media Gateway, никогда — upstream origin. */
    streamUrl: z.string().startsWith('/media/'),
    expiresAt: z.string(),
    container: z.enum(['hls', 'dash', 'progressive']),
    mimeType: z.string(),
    decision: playbackDecisionSchema,
    durationMs: z.number().int().nonnegative().nullable(),
    /** Позиция для продолжения просмотра, если она сохранена сервером. */
    resumePositionMs: z.number().int().nonnegative(),
    subtitles: z.array(playbackSubtitleSchema),
    /**
     * Человеческие названия звуковых дорожек по их порядку в потоке.
     *
     * Нужны потому, что сам поток называет дорожки `rus0`, `rus1`, … — по таким
     * подписям выбрать озвучку невозможно. Настоящие названия знает только
     * источник, и сервер передаёт их клиенту отдельно.
     *
     * Пустой список — источник названий не сообщил; тогда клиент показывает
     * дорожки так, как их назвал поток.
     */
    audioTracks: z.array(z.string()).default([]),
});
/**
 * Состояние ранее выданной сессии (`GET /playback/session/:id`).
 *
 * Отдельный DTO от `playbackSessionResponse` намеренно: тот описывает готовый
 * к воспроизведению поток и обязан содержать `streamUrl`. Здесь `streamUrl`
 * может быть `null` — сессия существует и привязана к контенту, но байты ещё
 * не проксируются. Так клиент отличает «нет потока» от подделки потока.
 *
 * Наружу отдаются только те идентификаторы, которые клиент сам и прислал:
 * upstream-ссылки, TorrServer handle и имя трекера сюда не попадают.
 */
/**
 * Сервисная информация о том, ЧТО и КАК сейчас играет — техпанель плеера.
 *
 * Здесь нет ни одного upstream-адреса: только человекочитаемое имя источника
 * (то же, что видно в списке выбора) и характеристики самого потока. Нужна
 * затем же, зачем такая же панель в Jellyfin: без неё «почему тормозит» и
 * «почему греется процессор» неотличимы друг от друга.
 */
export const playbackMediaInfoSchema = z.object({
    /** Прямая отдача или перекодирование — и чем именно оно делается. */
    mode: playbackModeSchema,
    hardware: z.enum(['vaapi', 'cpu', 'none']),
    /** Почему выбран этот режим; технический текст, но показывать его честно. */
    reason: z.string(),
    /** Подпись источника из списка выбора («Skaz · Rezka»); `null` — неизвестна. */
    source: z.string().nullable().default(null),
    /** Контейнер оригинального потока; известен без ffprobe по выбранному файлу/варианту. */
    container: z.string().nullable().default(null),
    videoCodec: z.string().nullable().default(null),
    audioCodec: z.string().nullable().default(null),
    height: z.number().int().positive().nullable().default(null),
    /** Битрейт источника, кбит/с. `null` — не измерен. */
    bitrateKbps: z.number().int().positive().nullable().default(null),
    /** Размер исходного файла; direct player может вычислить средний битрейт после metadata. */
    sourceSizeBytes: z.number().int().positive().nullable().default(null),
    /**
     * Принудительный потолок битрейта, если пользователь выбрал его сам. Его
     * наличие и означает принудительное перекодирование.
     */
    forcedBitrateKbps: z.number().int().positive().nullable().default(null),
    /**
     * Принудительный потолок высоты, если пользователь выбрал его сам. Как и
     * битрейт, его наличие означает принудительное перекодирование.
     */
    forcedHeight: z.number().int().positive().nullable().default(null),
});
export const playbackSessionStatusSchema = z.object({
    sessionId: playbackSessionIdSchema,
    contentId: contentIdSchema,
    sourceId: sourceIdSchema.nullable(),
    fileId: fileIdSchema.nullable(),
    expiresAt: z.string(),
    /** `false`, пока streaming pipeline не готов: ждать поток бессмысленно. */
    streamReady: z.boolean(),
    /** Всегда путь нашего Media Gateway; `null`, пока потока нет. */
    streamUrl: z.string().startsWith('/media/').nullable(),
    /** Названия звуковых дорожек по порядку; см. `playbackSessionResponse`. */
    audioTracks: z.array(z.string()).default([]),
    /**
     * Дорожки субтитров отдельными файлами; см. `playbackSessionResponse`.
     *
     * Повторяются здесь потому, что плеер читает состояние сессии и после
     * перезагрузки страницы — а тогда ответа на её создание у него уже нет.
     */
    subtitles: z.array(playbackSubtitleSchema).default([]),
    /**
     * Кто переключает звуковые дорожки.
     *
     * `client` — они едут внутри потока, и плеер меняет их сам. `server` — поток
     * перекодируется, в нём осталась одна сведённая дорожка, и выбор возможен
     * только до запуска ffmpeg (`POST /playback/session/:id/audio`). Клиенту
     * нужно это знать: в режиме `server` переключение в плеере не даёт ничего,
     * а пункты меню при этом есть.
     */
    audioSelection: z.enum(['client', 'server']).default('client'),
    /**
     * Какая серия играется. Плеер знает только `sessionId`, а прогресс хранится
     * по серии — номер он берёт отсюда. `null` — тайтл целиком (фильм).
     */
    season: z.number().int().nonnegative().nullable().default(null),
    episode: z.number().int().positive().nullable().default(null),
    /**
     * Чем в этом потоке перематывать.
     *
     * - `native` — плеер справится сам: прямая отдача с `Range` или VOD-плейлист,
     *   где шкалу задаёт сам плейлист;
     * - `restart` — шкалы у потока нет (непрерывный fMP4 из ffmpeg), но сервер
     *   умеет отдать его С НУЖНОЙ СЕКУНДЫ: плеер запрашивает `stream/t<sek>` и
     *   ведёт виртуальную шкалу поверх. Так перематываются копирующие режимы,
     *   которым посегментная нарезка противопоказана (PLAYBACK_TRANSCODING.md);
     * - `none` — перемотки нет: живой эфир либо источник, длительность которого
     *   неизвестна. Обещать её в UI нельзя.
     */
    seekStrategy: z.enum(['native', 'restart', 'none']).default('native'),
    /**
     * Полная длительность источника (мс) — основа виртуальной шкалы при
     * `seekStrategy: restart`: у самого потока длительности нет, он начинается с
     * запрошенной секунды. `null` — не измерена.
     */
    durationMs: z.number().int().positive().nullable().default(null),
    /** Сервисная информация для техпанели плеера; `null`, пока потока нет. */
    media: playbackMediaInfoSchema.nullable().default(null),
    /**
     * Позиция возобновления, как в `playbackSessionResponse`: считает её сервер,
     * правило одно на всех клиентов. Повторяется в статусе потому, что плеер
     * читает состояние сессии, когда ответа на её создание у него уже нет.
     */
    resumePositionMs: z.number().int().nonnegative().default(0),
});
/**
 * Что делает плеер прямо сейчас — по словам самого плеера.
 *
 * Сервер до сих пор угадывал это по потоку байт: идут — смотрят, не идут —
 * простой. Догадка врёт в обе стороны. Прямая отдача целого файла — это один
 * запрос на весь фильм, и «байты идут» там значит лишь то, что тело ответа ещё
 * не кончилось; поставленное на паузу видео с полным буфером байт не тянет
 * вовсе, хотя зритель сидит перед экраном. Никакой байт не отличит паузу от
 * выключенного телевизора, а буферизацию — от просмотра.
 *
 * Поэтому состояние называет клиент. Отчёт короткий и частый: он же и держит
 * сессию живой, пока плеер открыт.
 */
export const playbackHeartbeatSchema = z.object({
    state: z.enum(['playing', 'paused', 'buffering', 'ended']),
    /** Позиция на шкале источника — абсолютная, как её показывает плеер. */
    positionMs: z.number().int().nonnegative(),
    /** Полная длительность, если плеер её знает. */
    durationMs: z.number().int().positive().nullable().default(null),
});
/**
 * Структурированная ошибка плеера. Сервер не считает её доказательством
 * несовместимости: решение принимается по capability matrix и ffprobe-данным.
 */
export const playbackErrorReportSchema = z.object({
    code: z.enum([
        'UNSUPPORTED_CODEC',
        'UNSUPPORTED_CONTAINER',
        'DECODE_ERROR',
        'NETWORK_ERROR',
        'STALLED',
        'UNKNOWN',
    ]),
    component: z.enum(['video', 'audio', 'subtitle', 'network', 'player']).default('player'),
    value: z.string().max(120).optional(),
    fatal: z.boolean().default(false),
    positionMs: z.number().int().nonnegative().default(0),
    message: z.string().max(500).optional(),
});
/**
 * Класс источника в терминах UX, а не транспорта: `torrent` — раздача, которую
 * ещё нужно поднять в TorrServer, `online` — готовый онлайн-балансер,
 * `jellyfin` — готовый файл из внешней медиатеки Jellyfin (direct-play его
 * оригинала, перекодирование при необходимости — на нашей стороне). В UI
 * Jellyfin показывается в обоих разделах — «Торренты» (как готовый файл-релиз
 * рядом с раздачами) и «Смотреть онлайн» (как источник потока);
 * `kind` сохраняется отдельным, чтобы backend выбрал правильную подготовку.
 * Транспорт (`http`/`hls`) описывает поле `type` и не определяет раздел UI.
 */
export const providerSourceKindSchema = z.enum(['torrent', 'online', 'jellyfin']);
/** Нормализованный источник от provider plugin. Upstream URL сюда не попадает. */
export const providerSourceSchema = z.object({
    sourceId: sourceIdSchema,
    providerId: z.string(),
    kind: providerSourceKindSchema,
    /** Безопасное имя для UI; upstream host/tracker не попадает в DTO. */
    providerLabel: z.string().max(80).nullable().optional(),
    /**
     * Стабильный ключ источника и того, что внутри него, — то, чем администратор
     * задаёт порядок и что прячет.
     *
     * Отдельно от `providerLabel`, потому что имя НЕ стабильно: Skaz называет
     * витрину вместе с качеством именно этого тайтла («Filmix - 4K HDR»,
     * «Rezka ~720p ~ 720p») и со временем переименовывает её саму («FilmGE» →
     * «🇬🇪 FilmGE»). Настройка, записанная именем, от такого молча переставала
     * работать: одна витрина превращалась в несколько строк, и порядок задавала
     * та из них, что стояла выше.
     *
     * `groupKey` — сам источник (`skaz`, `hdvb`, `jellyfin`, трекер у раздач).
     * `variantKey` — что внутри него, с тем же префиксом (`skaz:filmixtv`);
     * `null` там, где второго уровня нет. Второй уровень есть только у Skaz:
     * витрины постоянны, а озвучки прочих балансеров меняются от тайтла к
     * тайтлу, и упорядочивать их нечем.
     */
    groupKey: z.string().max(64).default(''),
    variantKey: z.string().max(96).nullable().default(null),
    /**
     * Место источника в порядке, заданном администратором: меньше — выше в меню.
     * Считает сервер, потому что порядок — его настройка, а не вкус клиента.
     *
     * Клиенту он всё же нужен: онлайн-поиск прогрессивный, источники приходят
     * несколькими ответами, и склеенный список пришлось бы упорядочивать заново.
     * Без ранга витрина, приехавшая вторым ответом, падала бы в конец, даже если
     * администратор поставил её первой. Равные ранги сохраняют порядок прихода.
     */
    sourceRank: z.number().int().nonnegative().optional(),
    /**
     * Этой раздачей уже смотрели — этот зритель и с этого тайтла.
     *
     * Считает СЕРВЕР и по стабильному ключу релиза, а не по `sourceId`: у раздачи
     * он случайный на каждый поиск (короткоживущий грант), и отметка, сохранённая
     * клиентом по нему, не нашлась бы уже на следующем поиске. Наружу уходит
     * только результат сравнения — сам ключ остаётся на сервере.
     */
    played: z.boolean().default(false),
    /**
     * Это и есть тот источник, которым зритель смотрел этот тайтл прошлый раз.
     *
     * Считает СЕРВЕР, а не клиент. У онлайн-витрины `sourceId` стабилен, и раньше
     * клиент сверял его сам; у раздачи он живёт один поиск, и такая сверка не
     * находила ничего — приставка молча предлагала первую попавшуюся, а веб по
     * запасному пути (подписи) находил «какую-нибудь раздачу с того же трекера».
     * Правило одно и живёт там же, где ключ релиза, — на сервере.
     */
    remembered: z.boolean().default(false),
    type: z.enum(['torrent', 'http', 'hls', 'local']),
    title: z.string(),
    releaseName: z.string().max(240).nullable().optional(),
    quality: z.string().nullable(),
    languages: z.array(z.string()),
    voices: z.array(z.string()).default([]),
    availability: z.object({
        status: z.enum(['ready', 'checking', 'unavailable']).default('ready'),
        seeders: z.number().int().nonnegative().nullable().default(null),
    }).optional(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    /**
     * Потянет ли эта раздача просмотр — оценка сервера, а не вкус клиента.
     *
     * У раздачи два числа, и по отдельности они не значат ничего: «40 ГБ» — это
     * приговор для фильма и норма для сезона из двадцати серий, а «три
     * раздающих» — беда для ремукса и достаток для рипа. Смысл появляется только
     * в их отношении: сколько битрейта требует раздача и сколько его дадут
     * раздающие. Сервер это отношение и считает — он же знает хронометраж тайтла.
     *
     * Отсутствует у онлайн-витрин: там нет ни раздающих, ни размера, а скорость
     * определяет чужой балансер.
     */
    playability: z
        .object({
        /**
         * `good` — хватит с запасом, `tight` — впритык, `poor` — не хватит,
         * `unknown` — судить не по чему (нет размера, раздающих или хронометража).
         */
        verdict: z.enum(['good', 'tight', 'poor', 'unknown']),
        /** Битрейт раздачи, кбит/с: размер, поделённый на длительность. */
        bitrateKbps: z.number().int().nonnegative().nullable().default(null),
        /** Сколько битрейта, по нашей оценке, дадут раздающие, кбит/с. */
        speedKbps: z.number().int().nonnegative().nullable().default(null),
        /** Что сказать зрителю под строкой. `null` — сказать нечего. */
        note: z.string().max(160).nullable().default(null),
    })
        .optional(),
    files: z.array(z.object({
        fileId: fileIdSchema,
        name: z.string(),
        sizeBytes: z.number().int().nonnegative().nullable(),
        videoCodec: z.string().nullable(),
        audioLanguages: z.array(z.string()),
        hasSubtitles: z.boolean(),
    })),
});
export const providerSourcesResponseSchema = z.object({
    contentId: contentIdSchema,
    sources: z.array(providerSourceSchema),
    /** Честное объяснение пустого списка вместо тихого пустого ответа. */
    notice: z.string().nullable(),
    /**
     * Поиск ещё не завершён — часть источников догрузится позже (прогрессивный
     * онлайн-поиск). Клиент по флагу дозапрашивает тот же endpoint со спиннером и
     * склеивает источники по `sourceId`. Отсутствует/`false` — список финальный.
     */
    pending: z.boolean().optional(),
});
/** Озвучки одного источника (для отдельного выбора). Пустой список — выбора нет. */
export const sourceVoicesResponseSchema = z.object({
    sourceId: sourceIdSchema,
    voices: z.array(z.string()),
    /**
     * Озвучка, которую зритель выбирал у ЭТОГО источника; `null` — не выбирал.
     *
     * Считает сервер, потому что помнит он же — и помнит на пару «тайтл +
     * витрина». Одну и ту же студию витрины называют по-разному («LostFilm» в
     * Alloha, «LostFilm (aac)» в KinoPub), а сверка идёт точным именем: одна
     * строка на тайтл теряла выбор при каждой смене витрины.
     */
    remembered: z.string().nullable().default(null),
});
/** Диагностика транскодирования; используется Admin Panel и Telegram-ботом. */
export const transcodeCapabilityResponseSchema = z.object({
    ffmpeg: z.object({
        available: z.boolean(),
        version: z.string().nullable(),
        hwaccels: z.array(z.string()),
        vaapiSupport: z.boolean(),
    }),
    vaapi: z.object({
        /** Userspace-стек (libva + драйверы) присутствует в образе. */
        userspaceReady: z.boolean(),
        /** Найден ли render node `/dev/dri/renderD*`. */
        renderNodePresent: z.boolean(),
        renderNodes: z.array(z.string()),
        vainfoAvailable: z.boolean(),
        /**
         * `true` только если реальная инициализация VAAPI прошла успешно.
         * Отсутствие проверки НИКОГДА не выдаётся за успех.
         */
        hardwareVerified: z.boolean(),
        driver: z.string().nullable(),
        detail: z.string(),
    }),
    fallback: z.object({
        mode: z.enum(['vaapi', 'cpu', 'direct-only']),
        maxConcurrentTranscodes: z.number().int().nonnegative(),
        detail: z.string(),
    }),
});
/**
 * Что происходит, пока раздача поднимается.
 *
 * Пустой спиннер неотличим от зависшего приложения: раздача может искать пиров
 * полминуты и при этом быть совершенно живой. Живые цифры движка — раздающие,
 * скорость, сколько уже в кэше — единственное, что честно отвечает на вопрос
 * «оно вообще работает или я жду зря».
 */
export const playbackPreparationSchema = z.object({
    contentId: contentIdSchema,
    /**
     * `adding` — раздача добавляется в движок; `waiting-peers` — пиров ещё нет;
     * `buffering` — байты пошли, набираем начало файла.
     */
    stage: z.enum(['adding', 'waiting-peers', 'buffering']),
    /** Подпись источника — ровно та, что зритель выбрал в списке. */
    sourceLabel: z.string().max(120).nullable(),
    peers: z.number().int().nonnegative().nullable(),
    downloadSpeedBps: z.number().nonnegative().nullable(),
    loadedBytes: z.number().nonnegative().nullable(),
    updatedAt: z.string(),
});
export const playbackPreparationResponseSchema = z.object({
    /** `null` — ничего не готовится: либо уже готово, либо и не начиналось. */
    preparation: playbackPreparationSchema.nullable(),
});
//# sourceMappingURL=playback.js.map